import type { Document } from "../document/Document";
import type { Layer } from "../document/Layer";
import type { Command } from "./Command";
import { Selection } from "../selection/Selection";

/**
 * Atomically resize the canvas AND shift all layer positions.
 * Used by Trim Image and Reveal All — both need to move layers AND resize the
 * canvas in a single undoable step.  ResizeCanvasCommand alone computes dx=0,
 * dy=0 for "top-left" anchor, so the layer-shift is a separate explicit field
 * here.
 *
 * Undo restores the original canvas size AND the original layer positions.
 *
 * Photoshop equivalent: Image → Trim  /  Image → Reveal All.
 */
export class TrimRevealCommand implements Command {
  readonly kind = "trim-reveal";

  constructor(
    private readonly newWidth: number,
    private readonly newHeight: number,
    /** Amount to shift every layer (canvas origin displacement). */
    private readonly dx: number,
    private readonly dy: number,
  ) {}

  apply(doc: Document): Document {
    const layers = shiftLayers(doc.layers, this.dx, this.dy);
    const sel = translateAndResizeSelection(
      doc.selection,
      doc.width, doc.height,
      this.newWidth, this.newHeight,
      this.dx, this.dy,
    );
    return { ...doc, width: this.newWidth, height: this.newHeight, layers, selection: sel };
  }

  invert(before: Document, _after: Document): Command {
    // Inverse: restore canvas and shift layers back by (-dx, -dy).
    return new TrimRevealCommand(before.width, before.height, -this.dx, -this.dy);
  }
}

function shiftLayers(layers: readonly Layer[], dx: number, dy: number): Layer[] {
  return layers.map((l) => {
    if (l.kind === "group") {
      return { ...l, x: l.x + dx, y: l.y + dy, children: shiftLayers(l.children, dx, dy) };
    }
    return { ...l, x: l.x + dx, y: l.y + dy };
  });
}

function translateAndResizeSelection(
  sel: Selection | null,
  oldW: number, oldH: number,
  newW: number, newH: number,
  dx: number, dy: number,
): Selection | null {
  if (!sel || sel.isEmpty()) {
    return sel ? Selection.empty(newW, newH) : null;
  }
  if (newW <= 0 || newH <= 0) return Selection.empty(Math.max(0, newW), Math.max(0, newH));
  const next = new Uint8Array(newW * newH);
  for (let y = 0; y < newH; y++) {
    const sy = y - dy;
    if (sy < 0 || sy >= oldH) continue;
    for (let x = 0; x < newW; x++) {
      const sx = x - dx;
      if (sx < 0 || sx >= oldW) continue;
      next[y * newW + x] = sel.mask[sy * oldW + sx] ?? 0;
    }
  }
  return new Selection(newW, newH, next);
}
